/**
 * Registers a new user in the Sylius system and logs them in if successful.
 *
 * Sends a POST request to the Sylius API v2 `/api/customer/register` endpoint to create a new user.
 * If registration is successful, it automatically logs the user in using NextAuth.
 *
 * @param {string} email - The email address of the user.
 * @param {string} firstname - The first name of the user.
 * @param {string} lastname - The last name of the user.
 * @param {string} password - The password for the new account.
 *
 * @returns {Promise<{ success: boolean; message?: string; errors?: Record<string, string>; }>} 
 *          An object containing the success status, message, or validation errors.
 */
export async function RegisterUser(
  email: string,
  firstname: string,
  lastname: string,
  password: string,
): Promise<{
  success: boolean;
  message?: string;
  errors?: Record<string, string>;
  token?: string | undefined;
  customerId?: number | undefined;
}> {
  try {
    const response = await fetch("/api/customer/register", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email,
        firstName: firstname,
        lastName: lastname,
        password,
      }),
    });

    const data = await response.json();

    if (!data.success) {
      if (data.errors) {
        return { success: false, errors: data.errors };
      }
      if (data.error) {
        return { success: false, message: data.error };
      }
      return { success: false, message: "Registration failed." };
    }
    
    return { success: true, message: "Registration successful." };
  } catch {
    return { success: false, message: "An error occurred during registration." };
  }
}
